import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { Client } from '@stomp/stompjs';
import { useAlert } from 'contexts/AlertContext';
import { MapContainer, MapMarker } from 'components/map';
import { useUserLocation } from 'hooks/useUserLocation';
import { SafeReturnRepository, SafeReturnSessionResponse } from 'repository/safeReturnRepository';
import { ensureFreshAccessToken } from 'common/axios/axios';
import { getGatewayUrl } from 'common/axios/getApiUrl';
import { setNativeSafeReturnSession, clearNativeSafeReturnSession, setNativeMeetingSchedule, removeNativeMeetingSchedule, isNativeApp } from 'common/webview';
import {
    AppScreen, Badge, BadgeTone, Bleed, Button, ButtonFooter, Section, SectionHeader,
} from 'components/ds';
import styles from './SafeReturnSession.module.css';
import { haversineMeters } from 'common/utils/geo';


const formatRemain = (ms: number): string => {
    if (ms <= 0) return i18n.t('safeReturn.expired');
    const totalMin = Math.floor(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h > 0) return i18n.t('safeReturn.remainHM', { h, m });
    return i18n.t('safeReturn.remainM', { m });
};

const formatElapsed = (fromIso: string): string => {
    const diff = Date.now() - new Date(fromIso).getTime();
    if (diff < 0) return i18n.t('safeReturn.elapsedJustNow');
    const min = Math.floor(diff / 60000);
    if (min < 1) return i18n.t('safeReturn.elapsedJustNow');
    if (min < 60) return i18n.t('safeReturn.elapsedMin', { min });
    const h = Math.floor(min / 60);
    return i18n.t('safeReturn.elapsedHM', { h, m: min % 60 });
};

const calcDistanceM = haversineMeters;

// 위치 보고 주기 — traveler 전용
const REPORT_INTERVAL_MS = 60_000; // 1분

export const SafeReturnSession: React.FC = () => {
    const navigate = useNavigate();
    const { sessionId } = useParams<{ sessionId: string }>();
    const { t } = useTranslation();
    const { showAlert, showConfirm } = useAlert();
    const { location: myLocation, fetchLocation } = useUserLocation();

    const [session, setSession] = useState<SafeReturnSessionResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [travelerLocation, setTravelerLocation] = useState<{ lat: number; lng: number } | null>(null);
    const [nowMs, setNowMs] = useState<number>(Date.now());
    const [cancelling, setCancelling] = useState(false);
    const [respondBusy, setRespondBusy] = useState(false);

    const currentUserNo = Number(sessionStorage.getItem('userNo')) || 0;
    const stompRef = useRef<Client | null>(null);

    // 세션 로드
    const loadSession = async () => {
        if (!sessionId) return;
        try {
            const res: any = await SafeReturnRepository.onGetSession(sessionId);
            const data: SafeReturnSessionResponse | undefined = res?.data?.data || res?.data;
            if (!data || !data.sessionId) {
                setError(t('safeReturn.sessionLoadFailed'));
                return;
            }
            setSession(data);
            if (data.lastLat != null && data.lastLng != null) {
                setTravelerLocation({ lat: data.lastLat, lng: data.lastLng });
            }
        } catch (e) {
            setError(t('safeReturn.sessionLoadError'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSession();
    }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

    // 30초 주기 폴링 (watchers 상태 변화 반영 + STOMP 백업)
    useEffect(() => {
        if (!sessionId) return;
        const id = setInterval(() => { loadSession(); }, 30_000);
        return () => clearInterval(id);
    }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

    // 1초 tick — 남은 시간 표시
    useEffect(() => {
        const id = setInterval(() => setNowMs(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    const isTraveler = !!session && session.travelerUserNo === currentUserNo;
    const myWatch = session?.watchers.find(w => w.watcherUserNo === currentUserNo);
    const isActiveWatcher = !!myWatch && myWatch.status === 'ACCEPTED' && session?.status === 'ACTIVE';

    // 위치 확인 로그 — ACCEPTED watcher 가 진입한 시점에 SAFE_RETURN 타입으로 1회 기록.
    // 서버 UPSERT 라 같은 일자에 여러 번 들어와도 view_count 만 누적.
    useEffect(() => {
        if (!session || !isActiveWatcher) return;
        if (!session.meetingId || !session.travelerUserNo) return;
        import('repository/locationViewLogRepository').then(({ LocationViewLogRepository }) => {
            LocationViewLogRepository
                .onRecordViews(session.meetingId, [session.travelerUserNo], 'SAFE_RETURN')
                .catch(() => { /* 로그 실패 무시 */ });
        });
    }, [session?.meetingId, session?.travelerUserNo, isActiveWatcher]); // eslint-disable-line react-hooks/exhaustive-deps

    // STOMP 구독 — traveler 또는 ACCEPTED watcher
    useEffect(() => {
        if (!session || !sessionId) return;
        if (!isTraveler && !isActiveWatcher) return;
        if (session.status !== 'ACTIVE') return;

        // Sec-WebSocket-Protocol smuggling 으로 토큰 전달 — 게이트웨이가 추출/검증 후 X-User-No 주입
        // beforeConnect 로 매 (재)연결 직전 토큰 fresh 보장 — stale 토큰 무한 reconnect 회귀 방지
        const wsUrl = `${getGatewayUrl()}/dokocrew/ws`.replace(/^http/, 'ws');
        let cachedToken: string | null = null;
        const client = new Client({
            webSocketFactory: () => {
                const token = cachedToken || localStorage.getItem('accessToken');
                if (!token) console.warn('[STOMP-sr] 토큰 없음 — 익명 연결 시도');
                return new WebSocket(
                    wsUrl,
                    token ? ['v12.stomp', `bearer.${token}`] : ['v12.stomp'],
                );
            },
            reconnectDelay: 5000,
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,
            debug: (str) => console.log('[STOMP-sr]', str),
            beforeConnect: async () => {
                cachedToken = await ensureFreshAccessToken();
            },
            onConnect: () => {
                // 서버가 CONNECT JWT 에서 userNo 추출하여 traveler/watcher 권한 검증
                client.subscribe(
                    `/topic/safe-return/${sessionId}`,
                    (message) => {
                        try {
                            const update = JSON.parse(message.body);
                            if (typeof update.lat === 'number' && typeof update.lng === 'number') {
                                setTravelerLocation({ lat: update.lat, lng: update.lng });
                            }
                            // 상태 변경도 같은 토픽으로 올 수 있으므로 감지되면 리로드
                            if (update.status) {
                                loadSession();
                            }
                        } catch { /* ignore */ }
                    }
                );
            },
            onStompError: (frame) => {
                console.error('[SafeReturn STOMP] error', frame.headers['message']);
            },
        });
        client.activate();
        stompRef.current = client;

        return () => {
            if (stompRef.current) {
                stompRef.current.deactivate();
                stompRef.current = null;
            }
        };
    }, [session?.sessionId, session?.status, isTraveler, isActiveWatcher, sessionId, currentUserNo]); // eslint-disable-line react-hooks/exhaustive-deps

    // Traveler — 네이티브 앱에 BG location 유지 요청 (모임 종료와 무관하게 귀가 세션 동안 추적)
    // ACTIVE → 등록, 종료 상태(ARRIVED/EXPIRED/CANCELLED/DEAD_MAN) → 해제
    // 주의: 언마운트만으로는 해제하지 않는다 (세션 페이지를 벗어나도 BG tracking 유지해야 함)
    useEffect(() => {
        if (!session || !isTraveler) return;
        if (session.status === 'ACTIVE') {
            const expiresMs = new Date(session.expiresAt).getTime();
            if (Number.isFinite(expiresMs) && expiresMs > Date.now()) {
                setNativeSafeReturnSession({
                    sessionId: session.sessionId,
                    travelerUserNo: session.travelerUserNo,
                    destLat: session.destLat,
                    destLng: session.destLng,
                    destRadiusM: session.destRadiusM,
                    expiresAt: expiresMs,
                    role: 'TRAVELER',
                });
                // 모임 schedule endTime 을 expiresAt 까지 연장 → 모임 종료해도 LBS 보고 유지
                // 안전 귀가는 destLat/destLng 가 anchor 역할 (목적지 = 집/지정 장소)
                if (isNativeApp() && session.meetingId) {
                    setNativeMeetingSchedule(session.meetingId, Date.now(), expiresMs, {
                        meetingLat: session.destLat,
                        meetingLon: session.destLng,
                    });
                }
            }
        } else if (
            session.status === 'ARRIVED' ||
            session.status === 'EXPIRED' ||
            session.status === 'CANCELLED' ||
            session.status === 'DEAD_MAN'
        ) {
            clearNativeSafeReturnSession(session.sessionId);
            // 귀가 종료 → 연장했던 그 모임의 스케줄만 해제한다.
            // (전체 clear 는 참여 중인 다른 모임 추적까지 끊어버린다)
            if (isNativeApp() && session.meetingId) {
                removeNativeMeetingSchedule(String(session.meetingId));
            }
        }
    }, [isTraveler, session?.sessionId, session?.status, session?.expiresAt]); // eslint-disable-line react-hooks/exhaustive-deps

    // Traveler — 1분 주기 위치 보고
    useEffect(() => {
        if (!isTraveler || !session || session.status !== 'ACTIVE' || !sessionId) return;

        const report = async () => {
            const loc = await fetchLocation();
            if (!loc) return;
            try {
                await SafeReturnRepository.onReportLocation(sessionId, { lat: loc.lat, lng: loc.lng });
                setTravelerLocation({ lat: loc.lat, lng: loc.lng });
            } catch (e) {
                console.warn('[SafeReturn] 위치 보고 실패', e);
            }
        };
        // 즉시 1회
        report();
        const id = setInterval(report, REPORT_INTERVAL_MS);
        return () => clearInterval(id);
    }, [isTraveler, session?.status, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Watcher → Traveler 좌표 없을 때 본인 위치로 맵 중심 가이드
    useEffect(() => {
        if (!isTraveler && !myLocation) fetchLocation();
    }, [isTraveler, myLocation, fetchLocation]);

    const handleCancel = async () => {
        if (!sessionId) return;
        const ok = await showConfirm({ message: t('safeReturn.cancelConfirm') });
        if (!ok) return;
        setCancelling(true);
        try {
            await SafeReturnRepository.onCancelSession(sessionId);
            await showAlert({ message: t('safeReturn.cancelDone') });
            navigate(-1);
        } catch (e) {
            console.error('[SafeReturnSession] 취소 실패', e);
            showAlert({ message: t('safeReturn.cancelFailed') });
        } finally {
            setCancelling(false);
        }
    };

    const handleAccept = async () => {
        if (!sessionId) return;
        setRespondBusy(true);
        try {
            await SafeReturnRepository.onAcceptWatch(sessionId);
            await loadSession();
        } catch (e) {
            showAlert({ message: t('safeReturn.acceptFailed') });
        } finally {
            setRespondBusy(false);
        }
    };

    const handleDecline = async () => {
        if (!sessionId) return;
        setRespondBusy(true);
        try {
            await SafeReturnRepository.onDeclineWatch(sessionId);
            navigate(-1);
        } catch (e) {
            showAlert({ message: t('safeReturn.declineFailed') });
        } finally {
            setRespondBusy(false);
        }
    };

    const markers: MapMarker[] = useMemo(() => {
        if (!session) return [];
        const arr: MapMarker[] = [{
            id: 'dest',
            lat: session.destLat,
            lng: session.destLng,
            label: '',
            tooltip: session.destAddress || t('safeReturn.destLabel'),
            isDestination: true,
        }];
        if (travelerLocation) {
            arr.push({
                id: 'traveler',
                lat: travelerLocation.lat,
                lng: travelerLocation.lng,
                label: session.travelerNickname[0] || '?',
                color: '#FF9800',
                tooltip: session.travelerNickname,
                profileImgUrl: session.travelerProfileUrl || undefined,
            });
        }
        return arr;
    }, [session, travelerLocation]);

    const distanceToDest = useMemo(() => {
        if (!session || !travelerLocation) return null;
        return calcDistanceM(travelerLocation.lat, travelerLocation.lng, session.destLat, session.destLng);
    }, [session, travelerLocation]);

    const expiresMs = session ? new Date(session.expiresAt).getTime() - nowMs : 0;

    // 상태 텍스트
    const statusLabel = (() => {
        if (!session) return '';
        switch (session.status) {
            case 'ACTIVE': return t('safeReturn.statusActive');
            case 'ARRIVED': return t('safeReturn.statusArrived');
            case 'EXPIRED': return t('safeReturn.statusExpired');
            case 'DEAD_MAN': return t('safeReturn.statusDeadMan');
            case 'CANCELLED': return t('safeReturn.statusCancelled');
            default: return session.status;
        }
    })();

    /** 상태 배지 색 — 도착/진행/경보/종료 */
    const statusTone: BadgeTone = !session ? 'neutral'
        : session.status === 'ARRIVED' ? 'brand'
            : session.status === 'ACTIVE' ? 'weak'
                : (session.status === 'DEAD_MAN' || session.status === 'EXPIRED') ? 'danger'
                    : 'outline';

    const canAnswer = !!(myWatch && myWatch.status === 'INVITED' && session?.status === 'ACTIVE');

    return (
        <AppScreen title={t('safeReturn.sessionTitle')} bar="sub" hasFooter={canAnswer}>
            {loading && <p className={styles.state}>{t('safeReturn.loading')}</p>}
            {!loading && error && <p className={styles.state}>{error}</p>}

            {!loading && session && (
                <>
                    {/* 지금 어떤 상태인지 */}
                    <div className={styles.status}>
                        <Badge tone={statusTone}>{statusLabel}</Badge>
                        <span className={styles.statusSub}>
                            {session.status === 'ACTIVE' && formatRemain(expiresMs)}
                            {session.status === 'ARRIVED' && (session.arrivedAt
                                ? t('safeReturn.arrivedAtLabel', { time: formatElapsed(session.arrivedAt) })
                                : t('safeReturn.arrivedFallback'))}
                        </span>
                    </div>

                    {/* 어디로 가는지 + 얼마나 남았는지 */}
                    <Section spacing="tight">
                        <SectionHeader title={t('safeReturn.destLabel')} />
                        <p className={styles.dest}>{session.destAddress || t('safeReturn.noAddress')}</p>
                        {distanceToDest != null && session.status === 'ACTIVE' && (
                            <p className={styles.distance}>
                                {distanceToDest < 1000
                                    ? t('safeReturn.distanceRemainM', { m: Math.round(distanceToDest) })
                                    : t('safeReturn.distanceRemainKm', { km: (distanceToDest / 1000).toFixed(2) })}
                            </p>
                        )}
                        <Bleed>
                            <MapContainer
                                center={travelerLocation
                                    ? {
                                        lat: (travelerLocation.lat + session.destLat) / 2,
                                        lng: (travelerLocation.lng + session.destLng) / 2,
                                    }
                                    : { lat: session.destLat, lng: session.destLng }}
                                markers={markers}
                                height={300}
                                zoomLevel={4}
                                fitBounds={markers.length > 1}
                                placeName={session.destAddress}
                            />
                        </Bleed>
                        {!travelerLocation && session.status === 'ACTIVE' && (
                            <p className={styles.waiting}>
                                {t('safeReturn.waitingTraveler', { nickname: session.travelerNickname })}
                            </p>
                        )}
                    </Section>

                    {/* 끝난 뒤 안내 */}
                    {session.status === 'ARRIVED' && (
                        <Section divided>
                            <SectionHeader title={t('safeReturn.arrivedTitle')} />
                            <p className={styles.notice}>
                                {t('safeReturn.arrivedBody', {
                                    nickname: session.travelerNickname,
                                    radius: session.destRadiusM,
                                })}
                            </p>
                        </Section>
                    )}

                    {(session.status === 'DEAD_MAN' || session.status === 'EXPIRED') && (
                        <Section divided>
                            <SectionHeader
                                title={session.status === 'DEAD_MAN'
                                    ? t('safeReturn.deadManTitle')
                                    : t('safeReturn.expiredTitle')}
                            />
                            <p className={`${styles.notice} ${styles.noticeAlert}`}>
                                {t('safeReturn.alarmContact', { nickname: session.travelerNickname })}
                            </p>
                        </Section>
                    )}

                    {/* 지켜보는 사람 */}
                    <Section divided last>
                        <SectionHeader
                            title={t('safeReturn.watchersTitle')}
                            count={session.watchers.length}
                        />
                        <ul className={styles.people}>
                            {session.watchers.map(w => (
                                <li key={w.watcherId} className={styles.person}>
                                    <span className={styles.avatar}>
                                        {w.watcherProfileUrl
                                            ? <img src={w.watcherProfileUrl} alt="" />
                                            : (w.watcherNickname[0] || '?')}
                                    </span>
                                    <span className={styles.personName}>{w.watcherNickname}</span>
                                    <Badge tone={w.status === 'ACCEPTED' ? 'brand' : w.status === 'DECLINED' ? 'outline' : 'weak'}>
                                        {w.status === 'ACCEPTED' && t('safeReturn.watcherAccepted')}
                                        {w.status === 'DECLINED' && t('safeReturn.watcherDeclined')}
                                        {w.status === 'INVITED' && t('safeReturn.watcherInvited')}
                                    </Badge>
                                </li>
                            ))}
                        </ul>

                        {/* 내가 떠난 사람이면 여기서 끝낼 수 있습니다 */}
                        {isTraveler && session.status === 'ACTIVE' && (
                            <div className={styles.cancelAction}>
                                <Button variant="danger" size="lg" block onClick={handleCancel} disabled={cancelling}>
                                    {cancelling ? t('safeReturn.cancelling') : t('safeReturn.cancelTraveler')}
                                </Button>
                            </div>
                        )}
                    </Section>

                    {/* 아직 답 안 한 지켜보는 사람 */}
                    {canAnswer && (
                        <ButtonFooter>
                            <Button variant="ghost" size="lg" onClick={handleDecline} disabled={respondBusy}>
                                {t('safeReturn.decline')}
                            </Button>
                            <Button variant="primary" size="lg" block onClick={handleAccept} disabled={respondBusy}>
                                {t('safeReturn.accept')}
                            </Button>
                        </ButtonFooter>
                    )}
                </>
            )}
        </AppScreen>
    );
};

export default SafeReturnSession;
